// Copyright 2026 Zibby Lab. Apache-2.0.
//
// DestroyAppModal — styled destructive-confirm modal for tearing down a
// deployed app, replacing the bare native window.confirm. It serves both
// kinds of teardown:
//
//   • SOLO (dedicated EC2): irreversible data loss — terminating the VM
//     ALSO deletes its EBS volume AND its S3 (Litestream) backup. Those
//     callers pass `requireType` so the user must type the app slug to arm
//     the destroy button (a deliberate friction gate for data loss).
//
//   • CLOUD (Fargate): far less destructive — stopping a task + removing
//     the service. Those callers omit `requireType`, giving a plain styled
//     confirm (a DANGER button + Cancel, no type-to-confirm).
//
// Built on ConfirmModal (the codebase's centered dark dialog) — we omit
// its primary button and render our own gated DangerButton as children so
// we get a proper disabled state until the slug is typed correctly.

import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { trashOutline } from 'ionicons/icons';

import ConfirmModal from '../../molecules/ConfirmModal/ConfirmModal';
import DangerButton from '../DangerButton/DangerButton';
import './SoloDestroyModal.css';

export default function SoloDestroyModal({
  open,
  // `label` is the name/slug shown in the title + as the word to type.
  // `slug` is kept as a back-compat alias for the original solo callers.
  label,
  slug,
  // Optional explicit overrides; sensible defaults are derived below.
  title,
  warning,
  confirmLabel,
  busyLabel,
  // When true, the user must type `confirmWord` (defaults to label/slug)
  // before the destroy button arms — the solo data-loss gate. When
  // false/absent, the destroy button is armed immediately (cloud).
  requireType = false,
  confirmWord,
  busy = false,
  onClose,
  onConfirm,
}) {
  const name = label ?? slug ?? '';
  const word = confirmWord ?? name;

  const [typed, setTyped] = useState('');

  // Reset the typed value whenever the modal (re)opens so a prior attempt
  // doesn't leave it pre-armed.
  useEffect(() => {
    if (open) setTyped('');
  }, [open, word]);

  if (!open) return null;

  // Cloud (no type gate): armed as soon as it's open and not busy.
  // Solo (type gate): armed only once the typed value matches the word.
  const armed = !busy && (!requireType || typed.trim() === word);

  // Default copy: solo gets the explicit EBS+S3 data-loss warning; cloud
  // gets the lighter "stops the task" wording. Callers can override via
  // the `warning` / `title` props.
  const resolvedTitle = title
    ?? (requireType ? `Permanently delete ${name}?` : `Destroy ${name}?`);
  const resolvedWarning = warning ?? (requireType ? (
    <p style={{ color: 'rgba(255,255,255,0.78)', fontSize: 14, lineHeight: 1.55, marginTop: 0, marginBottom: 12 }}>
      This <strong style={{ color: '#fff' }}>terminates the VM</strong> and{' '}
      <strong style={{ color: '#ff6b6b' }}>DELETES ALL DATA</strong> — the EBS volume{' '}
      <strong style={{ color: '#fff' }}>AND</strong> the S3 (Litestream) backup.{' '}
      <strong style={{ color: '#fff' }}>This cannot be undone.</strong>
    </p>
  ) : (
    <p style={{ color: 'rgba(255,255,255,0.78)', fontSize: 14, lineHeight: 1.55, marginTop: 0, marginBottom: 12 }}>
      This stops the running task and removes the app.{' '}
      <strong style={{ color: '#fff' }}>This cannot be undone.</strong>
    </p>
  ));
  const resolvedConfirmLabel = confirmLabel ?? (requireType ? 'Permanently delete' : 'Destroy');
  const resolvedBusyLabel = busyLabel ?? (requireType ? 'Deleting…' : 'Destroying…');
  const confirmAria = requireType ? `Permanently delete ${name}` : `Destroy ${name}`;

  return (
    <ConfirmModal
      open={open}
      onClose={busy ? undefined : onClose}
      title={resolvedTitle}
      ariaLabel={requireType
        ? `Confirm permanent deletion of ${name}`
        : `Confirm destroying ${name}`}
      secondaryLabel=""
    >
      {resolvedWarning}

      {requireType && (
        <>
          <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: 13, lineHeight: 1.5, marginTop: 0, marginBottom: 8 }}>
            Type <strong style={{ color: '#fff' }}>{word}</strong> to confirm:
          </p>
          <input
            type="text"
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            disabled={busy}
            autoFocus
            spellCheck={false}
            autoCapitalize="off"
            autoCorrect="off"
            aria-label={`Type ${word} to confirm deletion`}
            placeholder={word}
            style={{
              width: '100%',
              boxSizing: 'border-box',
              padding: '10px 12px',
              marginBottom: 16,
              borderRadius: 8,
              border: '1px solid rgba(255,255,255,0.16)',
              background: 'rgba(0,0,0,0.3)',
              color: '#fff',
              fontSize: 14,
              fontFamily: 'inherit',
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && armed) onConfirm?.();
            }}
          />
        </>
      )}

      <div className="mkt-alert-modal-actions">
        {/* No explicit Cancel button — the modal's top-right ✕ and a
            backdrop click both dismiss (both wired to onClose), so Cancel
            was redundant. Removed per request. */}
        <DangerButton
          icon={trashOutline}
          disabled={!armed}
          onClick={() => { if (armed) onConfirm?.(); }}
          ariaLabel={confirmAria}
          className="solo-destroy-modal-confirm destroy-modal-confirm"
        >
          {busy ? resolvedBusyLabel : resolvedConfirmLabel}
        </DangerButton>
      </div>
    </ConfirmModal>
  );
}

SoloDestroyModal.propTypes = {
  open: PropTypes.bool,
  label: PropTypes.string,
  slug: PropTypes.string,
  title: PropTypes.string,
  warning: PropTypes.node,
  confirmLabel: PropTypes.string,
  busyLabel: PropTypes.string,
  requireType: PropTypes.bool,
  confirmWord: PropTypes.string,
  busy: PropTypes.bool,
  onClose: PropTypes.func,
  onConfirm: PropTypes.func,
};
